// Knowledge-Bank effectiveness (admin) — the weekly audit's scorecards.
//
// Renders what a run STORED (kb_effectiveness_runs.report / .headline via
// /api/admin/kb-effectiveness): the ranked findings, the per-profile
// scorecards, the run-over-run trend of the headline rates, the banks probe
// and the run ledger. The scorers in services/agent_console/kb_effectiveness
// are the single source of every number — this page never recomputes one.
// "Run audit now" writes a ledger row the worker picks up (admins only).

import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  kbEffectivenessApi,
  type RunDetail,
  type RunRead,
  type TrendPoint,
} from '../services/kbEffectiveness';
import { useAuth } from '../context/AuthContext';
import { BanksTable } from './kbEffectiveness/BanksTable';
import { FindingsList } from './kbEffectiveness/FindingsList';
import { ProfileScorecards } from './kbEffectiveness/ProfileScorecards';
import { RunHistory } from './kbEffectiveness/RunHistory';
import { TrendChart } from './kbEffectiveness/TrendChart';
import { whenUtc } from './kbEffectiveness/format';
import styles from './KBEffectivenessPage.module.css';

const ACTIVE_POLL_MS = 15_000;

export function KBEffectivenessPage() {
  const { user } = useAuth();
  const canRun = user?.role === 'ADMIN';
  const [runs, setRuns] = useState<RunRead[]>([]);
  const [detail, setDetail] = useState<RunDetail | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [trend, setTrend] = useState<TrendPoint[]>([]);
  const [profileId, setProfileId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [runNote, setRunNote] = useState<string | null>(null);
  const [scheduling, setScheduling] = useState(false);

  const load = useCallback(async (runId: string | null) => {
    setError(null);
    try {
      const [ledger, points, picked] = await Promise.all([
        kbEffectivenessApi.listRuns(20),
        kbEffectivenessApi.trend(26),
        runId ? kbEffectivenessApi.getRun(runId) : kbEffectivenessApi.latest(),
      ]);
      setRuns(ledger);
      setTrend(points);
      setDetail(picked);
      setSelectedId(picked?.id ?? null);
    } catch {
      setError('Could not load the KB effectiveness audit — is the API up and are you an admin?');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(null); }, [load]);

  // While a run is scheduled/running, refresh until it settles.
  const active = runs.some((r) => r.state === 'scheduled' || r.state === 'running');
  useEffect(() => {
    if (!active) return undefined;
    const t = window.setInterval(() => { void load(selectedId); }, ACTIVE_POLL_MS);
    return () => window.clearInterval(t);
  }, [active, load, selectedId]);

  const report = detail?.report ?? null;
  const profileIds = useMemo(() => report?.profiles.map((p) => p.profile_id) ?? [], [report]);
  useEffect(() => {
    if (profileIds.length && !profileIds.includes(profileId)) setProfileId(profileIds[0]);
  }, [profileIds, profileId]);

  const runNow = useCallback(async () => {
    setScheduling(true);
    setRunNote(null);
    try {
      const res = await kbEffectivenessApi.run();
      setRunNote(res.detail);
      await load(selectedId);
    } catch {
      setRunNote('Could not schedule the audit — are you an admin, and is the API up?');
    } finally {
      setScheduling(false);
    }
  }, [load, selectedId]);

  const warnings = detail?.detail.warnings ? Object.entries(detail.detail.warnings) : [];
  const flags = detail?.detail.red_flags?.filter((f) => f.flag_id) ?? [];

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div>
          <h1>Knowledge Bank effectiveness</h1>
          <p>
            Do the agents record what they learn, read the banks before they query the data, bank human
            corrections, and use their playbooks? Scored weekly from the Agent Console audit trail; a
            write-error or misrouted-write regression raises a MEDIUM RedFlag.
          </p>
        </div>
        {canRun ? (
          <button type="button" className={styles.runBtn} onClick={runNow} disabled={scheduling || active}>
            {active ? 'Audit running…' : scheduling ? 'Scheduling…' : 'Run audit now'}
          </button>
        ) : null}
      </div>
      {runNote ? <div className={styles.runNote}>{runNote}</div> : null}
      {error ? <div className={styles.error}>{error}</div> : null}
      {loading ? <div className={styles.empty}>Loading…</div> : null}

      {!loading && detail && report ? (
        <>
          <div className={styles.summary} data-testid="run-summary">
            <span>
              Showing run <code className={styles.profileTag}>{detail.id.slice(0, 8)}</code> ·{' '}
              {detail.trigger === 'manual' ? 'manual' : 'weekly beat'} · last {report.window_days} days ·
              finished {whenUtc(detail.finished_at)}
            </span>
            <span className={styles.meta}>
              {report.tool_calls.toLocaleString()} tool calls · {report.user_messages.toLocaleString()} user
              messages · {report.profiles.length} profiles
              {flags.length ? ` · ${flags.length} RedFlag${flags.length === 1 ? '' : 's'} raised` : ''}
            </span>
            {warnings.length ? (
              <span className={styles.warn}>
                Optional passes skipped: {warnings.map(([k, v]) => `${k} (${v})`).join('; ')}
              </span>
            ) : null}
          </div>
          <FindingsList findings={report.findings} />
          <ProfileScorecards profiles={report.profiles} findings={report.findings} />
          <section className={styles.panel} aria-label="Trend">
            <div className={styles.panelHead}>
              <h2>Trend across runs</h2>
              <label className={styles.meta}>
                profile{' '}
                <select value={profileId} onChange={(e) => setProfileId(e.target.value)} aria-label="Trend profile">
                  {profileIds.map((id) => <option key={id} value={id}>{id}</option>)}
                </select>
              </label>
            </div>
            <TrendChart points={trend} profileId={profileId} />
          </section>
          <BanksTable banks={report.banks} />
        </>
      ) : null}
      {!loading && !detail && !error ? (
        <div className={styles.empty}>
          No completed audit yet. The weekly beat runs every Monday 12:00 UTC
          {canRun ? ', or run it now.' : '.'}
        </div>
      ) : null}
      {!loading ? (
        <RunHistory runs={runs} selectedId={selectedId} onSelect={(id) => { void load(id); }} />
      ) : null}
    </div>
  );
}

export default KBEffectivenessPage;
